import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';

interface CustomDropdownProps {
    options: string[];
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    icon?: React.ReactNode;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ 
    options, 
    value, 
    onChange, 
    label, 
    placeholder = "Select an option",
    icon 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Reset focused index or set to current value when opening
    useEffect(() => {
        if (isOpen && value) {
            const currentIndex = options.indexOf(value);
            setFocusedIndex(currentIndex);
        } else if (!isOpen) {
            setFocusedIndex(-1);
        }
    }, [isOpen, value, options]);

    // Scroll focused item into view
    useEffect(() => {
        if (isOpen && focusedIndex >= 0 && listRef.current) {
            const list = listRef.current;
            const item = list.children[focusedIndex] as HTMLElement;
            if (item) {
                const listRect = list.getBoundingClientRect();
                const itemRect = item.getBoundingClientRect();

                if (itemRect.bottom > listRect.bottom) {
                    list.scrollTop += itemRect.bottom - listRect.bottom;
                } else if (itemRect.top < listRect.top) {
                    list.scrollTop -= listRect.top - itemRect.top;
                }
            }
        }
    }, [focusedIndex, isOpen]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev => (prev < options.length - 1 ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev => (prev > 0 ? prev - 1 : options.length - 1));
                break;
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0) {
                    onChange(options[focusedIndex]);
                    setIsOpen(false);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                break;
            case 'Tab':
                setIsOpen(false);
                break;
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div 
            className={`tw-relative tw-w-full ${isOpen ? 'tw-z-[100]' : 'tw-z-10'}`} 
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
        >
            {label && (
                <label className="tw-block tw-text-xs tw-font-bold tw-uppercase tw-text-gray-500 tw-mb-2">
                    {label}
                </label>
            )}
            
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-xl tw-px-4 tw-py-3 tw-text-left tw-transition-all tw-duration-300 focus:tw-border-[#00C5FB] tw-outline-none
                    ${isOpen ? 'tw-border-[#00C5FB] tw-ring-1 tw-ring-[#00C5FB]/20' : ''}
                `}
            >
                <div className="tw-flex tw-items-center tw-gap-3">
                    {icon && <span className="tw-text-gray-400">{icon}</span>}
                    <span className={value ? 'tw-text-white' : 'tw-text-gray-500'}>
                        {value || placeholder}
                    </span>
                </div>
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <ChevronDown className="tw-w-4 tw-h-4 tw-text-gray-500" />
                </motion.div>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                        className="tw-absolute tw-z-50 tw-w-full tw-mt-2 tw-bg-[#0A0A0A] tw-border tw-border-white/10 tw-rounded-xl tw-shadow-2xl tw-overflow-hidden tw-backdrop-blur-xl"
                    >
                        <div 
                            ref={listRef}
                            role="listbox"
                            className="tw-max-h-60 tw-overflow-y-auto tw-py-2 custom-scrollbar"
                        >
                            {options.map((option, index) => (
                                <button
                                    key={option}
                                    type="button"
                                    role="option"
                                    aria-selected={value === option}
                                    onClick={() => {
                                        onChange(option);
                                        setIsOpen(false);
                                    }}
                                    onMouseEnter={() => setFocusedIndex(index)}
                                    className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-text-sm tw-transition-all tw-duration-200 tw-relative
                                        ${focusedIndex === index ? 'tw-bg-[#00C5FB]/10 tw-text-white' : 'tw-text-gray-400'}
                                        ${value === option ? 'tw-text-[#00C5FB] tw-bg-[#00C5FB]/5' : ''}
                                    `}
                                >
                                    {focusedIndex === index && (
                                        <motion.div 
                                            layoutId="focus-accent"
                                            className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-1 tw-bg-[#00C5FB]"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.2 }}
                                        />
                                    )}
                                    <span className="tw-relative tw-z-10">{option}</span>
                                    {value === option && (
                                        <Check className="tw-w-4 tw-h-4 tw-relative tw-z-10" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CustomDropdown;
